import { useState } from 'react';
import { MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface SaveTurfDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  houseCount: number;
  voterCount: number;
}

export function SaveTurfDialog({
  open,
  onClose,
  onSave,
  houseCount,
  voterCount,
}: SaveTurfDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Turf name is required.');
      return;
    }
    onSave(trimmed);
    handleClose();
  }

  function handleClose() {
    setName('');
    setError('');
    onClose();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save New Turf</DialogTitle>
          <DialogDescription>
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {houseCount} houses &middot; {voterCount} voters
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <label htmlFor="turf-name" className="text-sm font-medium">
            Turf Name
          </label>
          <Input
            id="turf-name"
            placeholder="e.g. Oak Street Block 3"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Turf</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
